export interface IPunchlistDashboard {
  punchListId: number;
  neighborhoodId: number;
  punchListName: string;
  neighborhoodName: string;
  isArchived: boolean;
  punchListItems: IPunchlistItem[];
}

export interface IPunchlistTopLevel {
  punchListId: number;
  punchListName: string;
  punchListItems: IPunchlistItem[];
}

export interface IPunchlist {
  punchListId: number;
  punchListName: string;
  neighborhoodId: number;
  neighborhoodName: string;
  isComplete: boolean;
  updatedOn: string;
  inputValue?: string;
  territoryId: number;
  sageJobId: number;
}

export interface ITopLevelJobPunchlist {
  sageJobId: number;
  punchLists: ITopLevelPunchlist[];
}

export interface ITopLevelPunchlist {
  punchListId: number;
  punchListName: string;
  lastUpdated: string;
}

export interface INeighborhoodPunchlist {
  neighborhoodName: string;
  neighborhoodId: number;
  jobs: ITopLevelJobPunchlist[];
}

export interface IPunchlistPost {
  neighborhoodId: number;
  name: string;
  description?: string;
  purchaseOrderId?: number;
  sageJobId: number;
  punchlistTemplateId?: number;
  toDoItems?: IPunchlistUpdateTodoItem[];
}

export interface IPunchlistTodoItem {
  punchListItemId: number;
  description: string;
  itemStatuses: string;
  partnerId: number;
  assignedTo: string;
  assignedToDisplayName: string;
  completedOn: string;
  completedBy: string;
  completedByDisplayName: string;
  vendorName: string | null;
  vendorSageId: string | number | null;
  lotNumber: number | string;
  isComplete: boolean;
  dueDate: string;
  createdOn: string;
  subscribers: string[];
  vendorSubscribers: { vendorId: number; vendorName: string }[];
  sortOrder: number;
  punchlistItemFiles: IPunchlistTodoItemFile[];
  hasComments: boolean;
  photoGuids: string[];
  itemIsAssociatedToFwtOrChecklist: boolean;
  vendorCompletedOn: string;
  vendorCompletedBy: string;
  vendorCompletedByDisplayName: string;
}

export interface IPunchlistTodoItemFile {
  createdOn: string;
  fileName: string;
  fileUrl: string;
  punchlistItemFileId: number;
  punchlistItemId: number;
}

export interface IPunchlistTodoItemDetail {
  punchlistItemId: number;
  punchlistId: number;
  description: string;
  partnerId: number;
  assignedTo: string;
  assignedToDisplayName: string;
  statusId: number;
  punchlistItemTypeId: number;
  lotNumber: number;
  createdOn: string;
  createdBy: string;
  updatedOn: string;
  updatedBy: string;
  checklistItemType: number;
  checklistItemId: number;
  dueDate: string;
  comments: IPunchlistItemComment[];
  subscribers: string[];
  vendorSubscribers: { vendorId: number; vendorName: string }[];
  vendorSageId: number | null;
  punchlistItemFiles: IPunchlistTodoItemFile[];
  territoryId: number;
  sortOrder: number;
  sageJobId: number;
  itemIsAssociatedToFwtOrChecklist: boolean;
}

export interface IPunchlistTerritoryNeighborhood {
  neighborhoodName: string;
  neighborhoodId: number;
}

export interface IPunchlistTerritory {
  territoryId: number;
  territoryName: string;
  neighborhoods: IPunchlistTerritoryNeighborhood[];
}

export interface INeighborhoodOverview {
  neighborhoodId: number;
  neighborhoodName: string;
  dates: INeighborhoodOverviewDates[];
}

export interface INeighborhoodOverviewDates {
  date: string;
  dateDisplay: string;
  punchListItems: IPunchlistItem[];
}

export interface INeighborhoodPunchlistUser {
  username: string | null;
  lastActivity: string;
}

export interface IPunchlistItem {
  punchlistId: number;
  punchListItemId: number;
  punchListItemDescription: string;
  punchlistName: string;
  description: string;
  status: string;
  itemType: string;
  partnerId: number | null;
  neighborhoodDescription: string;
  lotDescription: string;
  assignedTo: string;
  assignedToDisplayName: string;
  completedBy: string;
  completedByDisplayName: string;
  lastUpdated: string;
  lastUpdatedDate: string;
  comments: IPunchlistItemComment[];
  itemIsAttachedToFwtOrChecklist: boolean;
}

export interface IPunchlistDetail {
  punchListId: number;
  name: string;
  description: string;
  neighborhoodId: number;
  sageJobId: number;
  neighborhoodName: string;
  isComplete: boolean;
  updatedOn: string;
  createdOn: string;
  isDirty: boolean;
  territoryId: number;
  isArchived: boolean;
  punchListItems: IPunchlistTodoItem[];
}

export interface IPunchlistUpdateTodoItem {
  punchListItemId?: number;
  description: string;
  partnerId: number | null;
  assignedTo: string;
  isComplete: boolean;
  dueDate: string;
  isDirty?: boolean;
  subscribers: string[];
  sortOrder: null | number;
  vendorSubscribers: number[];
  photoGuids: string[];
  shouldSendCreationEmail: boolean;
}
export interface IPunchlistItemCommentFile {
  punchlistItemCommentFileId: number;
  fileUrl: string;
  createdOn: string;
  createdBy: string;
  originalFileName: string;
}
export interface IPunchlistItemComment {
  punchlistItemCommentsId: number;
  comment: string;
  createdOn: string;
  createdBy: string;
  files: IPunchlistItemCommentFile[];
}

export interface IPunchlistSearchResult {
  punchListId: number;
  punchListName: string | null;
  territory: string | null;
  neighborhood: string | null;
  punchListItemDescription: string | null;
  itemType: ItemTypeValue;
  createdBy: string | null;
  createdOn: string;
  fileName: string | null;
}

export enum ItemType {
  TODO_ITEM = 'TodoItem',
  COMMENT = 'Comment',
  FILE_UPLOAD = 'FileUpload'
}
export type ItemTypeValue = 'TodoItem' | 'Comment' | 'FileUpload';

export interface IPunchlistTodo {
  punchListId: number;
  punchListName: string;
  territory: string;
  neighborhood: string;
  lots: IPunchlistTodoLot[];
}

export interface IPunchlistTodoLot {
  lotNumber: string;
  toDoItems: IPunchlistTodoLotTodoItem[];
}

export interface IPunchlistTodoLotTodoItem {
  punchListItemId: number;
  description: string;
  assignedTo: string;
  itemStatuses: string;
  dueDate: string;
  lastUpdated: string;
  isComplete: boolean;
  partnerId: number | null;
  subscribers: string[];
  vendorSubscribers: { vendorId: number; vendorName: string }[];
  punchlistItemFiles: IPunchlistTodoItemFile[];
  sortOrder: number;
  itemIsAssociatedToFwtOrChecklist: boolean;
}

export interface IUpdatePunchlistTodo {
  description: string;
  assignedTo: string;
  subscribers: string[];
  vendorSubscribers: number[];
  dueDate: string;
  isComplete: boolean;
  partnerId: number | null;
  photoGuids: string[];
  sortOrder: number;
}

export interface IPunchlistItemsReport {
  punchListId: number;
  punchListItemId: number;
  territory: string;
  neighborhood: string;
  vendorName: string;
  title: string;
  dueDate: string;
}

export interface IPunchlistNeighborhoodPerformance {
  territoryId: number;
  territory: string;
  neighborhoodId: number;
  neighborhood: string;
  itemCount: number;
}

export interface IPunchlistVendorPerformance {
  vendorId: number;
  vendorName: string;
  itemCount: number;
}

export interface IPunchlistAssignToLookup {
  id: number | string;
  accountName: string;
  displayName: string;
  personId: number;
  partnerId: number;
  isInternalUser: boolean;
}

export interface IPunchlistAssignToLookupSlim {
  description: string;
  shorthand: string;
  value: string;
}
